import { isDefined } from "@clipboard-health/util-ts";
// TODO: Use our own Avatar
import { Skeleton } from "@mui/material";
import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type CoreMessageType, type SendableMessageType } from "@sendbird/uikit-react/types/utils";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, parseISO } from "date-fns";
import type React from "react";

import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import { transformPlacementData, useGetPlacement } from "../../Placements/api/useGetPlacement";
import {
  type ChatMessageWithText,
  isChatMessageWithText,
  isSendBirdGroupChannel,
  type PlacementChatMetadata,
} from "../types";
import { PlacementChatMessageLayout } from "./PlacementChatMessageLayout";

function getInterviewTypeText(isWorkplaceProspect: boolean) {
  return isWorkplaceProspect ? "walk-in slot" : "interview";
}

function PlacementChatMessageContent({
  chatMessage,
  chatChannel,
}: {
  chatMessage: ChatMessageWithText;
  chatChannel: GroupChannel;
}) {
  const { userId: workerId } = useDefinedWorker();
  const { facilityName, placementTitle, placementId } =
    chatChannel.cachedMetaData as PlacementChatMetadata;

  const { data: placement, status } = useGetPlacement({
    placementId: placementId ?? "",
    workerId,
  });

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  if (!chatMessage.message || !placementId) {
    return undefined as unknown as React.ReactElement;
  }

  const transformedPlacement = placement
    ? transformPlacementData(placement, credentialRequirementConfiguration)
    : undefined;

  const defaultMessage =
    facilityName && placementTitle
      ? `Congrats! You've successfully applied for the ${placementTitle} opening at ${facilityName}.\n\nIf the facility likes your profile they'll reach out to you on this channel to set up an interview!\n\nYou can also use this channel to message the facility directly about anything related to their job listing.`
      : chatMessage.message;

  const isWorkplaceProspect = Boolean(
    transformedPlacement?.workplace.placementSettings?.isProspect
  );

  const interview = transformedPlacement?.interviews.find(
    (interview) => interview.attributes.status === InterviewStatus.BOOKED
  );

  // we cancel previous interview and create a new booked one in case of a reschedule
  // therefore we need to check for absence of a booked interview and presence of a cancelled interview
  const cancelledInterview =
    !isDefined(interview) &&
    transformedPlacement?.interviews.find(
      (interview) => interview.attributes.status === InterviewStatus.CANCELLED
    );
  let messageToDisplay = defaultMessage;

  const interviewTypeText = getInterviewTypeText(isWorkplaceProspect);

  const formattedAddress =
    transformedPlacement?.interviewSettings?.attributes.interviewAddress?.address.formatted ??
    transformedPlacement?.address?.formatted;
  if (transformedPlacement && interview && facilityName && placementTitle && formattedAddress) {
    messageToDisplay = `Thank you for booking ${
      isWorkplaceProspect ? "a" : "an"
    } ${interviewTypeText} for the ${placementTitle} opening at ${facilityName}.\n\nYour ${interviewTypeText} is scheduled for ${format(
      parseISO(interview.attributes.start),
      "MM/dd/yyyy"
    )} at ${format(parseISO(interview.attributes.start), "h:mm a")}.\n\nLocation: ${
      formattedAddress
    }`;
  } else if (cancelledInterview) {
    messageToDisplay = `Your ${interviewTypeText} at ${facilityName} scheduled for ${format(
      parseISO(cancelledInterview.attributes.start),
      "MM/dd/yyyy"
    )} at ${format(parseISO(cancelledInterview.attributes.start), "h:mm a")} was cancelled.\n\n${
      formattedAddress ? `Location: ${formattedAddress}` : ""
    }`;
  }

  const message = (
    <>
      {status === "loading" && <Skeleton variant="text" width="100%" height="24px" />}
      {status === "success" && messageToDisplay.trim()}
      {status === "error" && defaultMessage.trim()}
    </>
  );

  return (<PlacementChatMessageLayout message={message} />) as unknown as React.ReactElement;
}

export function PlacementChatMessage({
  message,
  channel,
}: {
  message: CoreMessageType;
  channel: unknown;
}) {
  const chatMessage = (
    typeof message === "object" && message !== null
      ? message
      : // FIXME: Automatically disabled during ESLint config update
        JSON.parse(JSON.stringify(message))
  ) as unknown;

  const chatChannel = (
    typeof channel === "object" && channel !== null
      ? channel
      : // FIXME: Automatically disabled during ESLint config update
        JSON.parse(JSON.stringify(channel))
  ) as unknown;

  if (!isChatMessageWithText(chatMessage) || !isSendBirdGroupChannel(chatChannel)) {
    return null as unknown as React.ReactElement;
  }

  if (
    chatMessage.customType === "PLACEMENT_APPLICATION" &&
    chatChannel.customType === "placements"
  ) {
    return <PlacementChatMessageContent chatMessage={chatMessage} chatChannel={chatChannel} />;
  }

  // the message user could be the managing (parent) facility, but this would be confusing for users
  // so we overwrite the name to show the true facility name
  const placementChatMetadata = chatChannel.cachedMetaData as PlacementChatMetadata;
  const { sender } = message as SendableMessageType;
  if (sender && placementChatMetadata.facilityName) {
    sender.nickname = placementChatMetadata.facilityName;
    sender.friendName = placementChatMetadata.facilityName;
  }

  return null as unknown as React.ReactElement;
}
